import React, { useMemo } from 'react';
import { Upload } from 'lucide-react';
import './HeroHomeEditor.css';

function deepClone(o) {
  try {
    return JSON.parse(JSON.stringify(o || {}));
  } catch {
    return {};
  }
}

function mergeContent(comp, changes) {
  const raw =
    typeof comp.content === 'string'
      ? JSON.parse(comp.content || '{}')
      : deepClone(comp.content);
  const ch = changes[comp.id] || {};
  Object.entries(ch).forEach(([pathStr, val]) => {
    if (pathStr === '__mediaId__') return;
    const parts = pathStr.split('.');
    let cur = raw;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      const next = parts[i + 1];
      if (cur[p] == null) cur[p] = /^\d+$/.test(String(next)) ? [] : {};
      cur = cur[p];
    }
    cur[parts[parts.length - 1]] = val;
  });
  return raw;
}

const JOURNAL_KEYS = [
  'title',
  'labelTitle',
  'valueTitle',
  'labelLanguage',
  'valueLanguage',
  'labelLength',
  'valueLength',
  'labelFee',
  'valueFee',
  'labelConfFee',
  'valueConfFee',
];

const TEMPLATE_KEYS = [
  'title',
  'step1Before',
  'step1LinkText',
  'step1After',
  'guidelinesPath',
  'step2Before',
  'step2LinkText',
  'step2After',
  'easyChairUrl',
  'step3',
  'step4',
  'step5',
];

export default function ArticleSubmissionEditor({ component: comp, languages, changes, onUpdate }) {
  const codes = (languages || []).map((l) => l.code);
  const langs = codes.length ? codes : ['en', 'ru', 'uz'];

  const content = useMemo(() => mergeContent(comp, changes), [comp, changes]);

  const setPageTitle = (code, value) => onUpdate(comp.id, [code, 'pageTitle'], value);
  const setJournal = (code, key, value) => onUpdate(comp.id, [code, 'journalCard', key], value);
  const setTemplate = (code, key, value) => onUpdate(comp.id, [code, 'templatesCard', key], value);

  return (
    <div className="hero-home-editor">
      <div className="hero-home-editor__head">
        <Upload size={22} />
        <div>
          <h3 className="hero-home-editor__title">Подача статьи</h3>
          <p className="hero-home-editor__hint">
            Статическая страница <code>/article-submission</code> — заголовок, блок о журнале и шаги с
            шаблонами.
          </p>
        </div>
      </div>

      <section className="hero-home-section">
        <h4>Заголовок страницы</h4>
        <div className="hero-lang-grid">
          {langs.map((code) => (
            <label key={`pt-${code}`} className="hero-lang-field">
              <span className="hero-lang-code">{code.toUpperCase()}</span>
              <input
                type="text"
                value={content[code]?.pageTitle ?? ''}
                onChange={(e) => setPageTitle(code, e.target.value)}
                placeholder="Article Submission"
              />
            </label>
          ))}
        </div>
      </section>

      <section className="hero-home-section">
        <h4>Карточка «Журнал» (journalCard)</h4>
        <div className="hero-lang-grid" style={{ gap: '1rem' }}>
          {langs.map((code) => (
            <div
              key={`jc-${code}`}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '0.85rem',
                background: '#fafafa',
              }}
            >
              <div className="hero-lang-code" style={{ marginBottom: '0.5rem' }}>
                {code.toUpperCase()}
              </div>
              {JOURNAL_KEYS.map((key) => (
                <label key={key} className="hero-lang-field" style={{ marginBottom: '0.45rem' }}>
                  <span className="hero-lang-code" style={{ fontSize: '0.65rem', minWidth: '5rem' }}>
                    {key}
                  </span>
                  <input
                    type="text"
                    value={content[code]?.journalCard?.[key] ?? ''}
                    onChange={(e) => setJournal(code, key, e.target.value)}
                    style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </label>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="hero-home-section">
        <h4>Карточка «Шаблоны и шаги» (templatesCard)</h4>
        <div className="hero-lang-grid" style={{ gap: '1rem' }}>
          {langs.map((code) => (
            <div
              key={`tc-${code}`}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '0.85rem',
                background: '#fafafa',
              }}
            >
              <div className="hero-lang-code" style={{ marginBottom: '0.5rem' }}>
                {code.toUpperCase()}
              </div>
              {TEMPLATE_KEYS.map((key) => (
                <label key={key} className="hero-lang-field" style={{ marginBottom: '0.45rem' }}>
                  <span className="hero-lang-code" style={{ fontSize: '0.65rem', minWidth: '5rem' }}>
                    {key}
                  </span>
                  <input
                    type="text"
                    value={content[code]?.templatesCard?.[key] ?? ''}
                    onChange={(e) => setTemplate(code, key, e.target.value)}
                    style={{ width: '100%', padding: '0.4rem 0.5rem', borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </label>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
